import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Printer, Sparkles, Share2, Check, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function Bar({ label, pct, band, color }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-xs font-semibold text-muted-foreground">{band ? `${band} · ` : ""}{pct}%</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color || "hsl(var(--primary))" }} />
      </div>
    </div>
  );
}

export default function ResultDetail({ shared = false }) {
  const { id, token } = useParams();
  const { t, lang } = useLang();
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const url = shared ? `/api/shared/${token}` : `/api/results/${id}`;
      const { data } = await api.get(url);
      setResult(data);
      return data;
    } catch {
      setError("not-found");
      return null;
    }
  }, [id, token, shared]);

  useEffect(() => { load(); }, [load]);

  const doShare = async () => {
    try {
      const { data } = await api.post(`/api/results/${id}/share`);
      setShareUrl(data.url);
      await navigator.clipboard.writeText(data.url).catch(() => {});
      setCopied(true);
      toast.success("Share link copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const downloadPdf = async () => {
    const node = pdfRef.current;
    if (!node) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        ignoreElements: (el) => el.classList && el.classList.contains("no-print"),
      });
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = margin;
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(imgData, "JPEG", margin, position, imgW, imgH);
      heightLeft -= pageH - margin * 2;
      while (heightLeft > 0) {
        position = margin - (imgH - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, position, imgW, imgH);
        heightLeft -= pageH - margin * 2;
      }
      const who = (result.studentName || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      pdf.save(`edvia-report-${who}-${langKey}.pdf`);
      toast.success(t("res.pdfReady"));
    } catch (err) {
      toast.error(t("res.pdfError"));
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    const anyPending = result && (result.reportStatus === "pending" ||
      Object.values(result.reportLangs || {}).includes("pending"));
    if (!anyPending) return;
    const iv = setInterval(async () => {
      const d = await load();
      const stillPending = d && (d.reportStatus === "pending" ||
        Object.values(d.reportLangs || {}).includes("pending"));
      if (!stillPending) clearInterval(iv);
    }, 4000);
    return () => clearInterval(iv);
  }, [result, load]);

  if (error) return <div className="mx-auto max-w-2xl px-4 py-20 text-center text-muted-foreground" data-testid="result-not-found">Result not found.</div>;
  if (!result) return <div className="flex min-h-[50vh] items-center justify-center" data-testid="result-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const isNew = result.engine === "edviaiq";
  const arch = result.archetype || {};
  const suffix = lang === "hi" ? "Hi" : lang === "ta" ? "Ta" : "";
  const pick = (obj, base) => (obj && (obj[base + suffix] || obj[base])) || "";
  const langKey = lang === "hi" ? "hi" : lang === "ta" ? "ta" : "en";
  const langField = { en: "aiReport", hi: "aiReport_hi", ta: "aiReport_ta" }[langKey];
  const report = result[langField];
  const reportLangs = result.reportLangs || {};
  const langStatus = reportLangs[langKey] || (report ? "ready" : "none");
  const langName = { en: "English", hi: "हिंदी", ta: "தமிழ்" }[langKey];
  const pendingSince = (result.reportPendingSince || {})[langKey];
  const pendingMs = langStatus === "pending" && pendingSince
    ? Date.now() - new Date(pendingSince).getTime() : 0;
  const stuckTooLong = langStatus === "pending" && pendingSince && pendingMs > 150000;

  return (
    <div className="mx-auto max-w-4xl px-4 py-12" data-testid="result-detail-page">
      <div ref={pdfRef} className="bg-background">
      {isNew ? (
        <div className="rounded-3xl border border-border bg-card p-8 shadow-sm print-full" data-testid="archetype-hero">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{result.studentName}</span>
              <h1 className="font-display mt-2 text-4xl font-semibold sm:text-5xl" style={{ color: arch.color }} data-testid="archetype-name">
                {pick(arch, "name")}
              </h1>
              <p className="mt-2 text-base font-medium text-muted-foreground">{pick(arch, "tagline")}</p>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{pick(arch, "description")}</p>
              {result.runnerUp && (
                <p className="mt-4 text-xs text-muted-foreground" data-testid="runner-up">
                  {t("res.also")}: <span className="font-semibold text-foreground">{pick(result.runnerUp, "name")}</span> ({result.runnerUp.match}%)
                </p>
              )}
            </div>
            <div className="flex h-28 w-28 shrink-0 flex-col items-center justify-center rounded-full border-4 text-center" style={{ borderColor: arch.color }} data-testid="archetype-match">
              <span className="font-display text-3xl font-semibold" style={{ color: arch.color }}>{arch.match}%</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("res.match")}</span>
            </div>
          </div>
          {arch.areas && (
            <div className="mt-6 flex flex-wrap gap-2">
              {(lang === "ta" && arch.areasTa ? arch.areasTa : arch.areas).map((a) => <span key={a} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">{a}</span>)}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-3xl border border-border bg-card p-8 print-full" data-testid="legacy-hero">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{result.studentName}</span>
          <h1 className="font-display mt-2 text-5xl font-semibold text-primary" data-testid="legacy-type">{result.type}</h1>
          {result.aiDeepDive && <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{result.aiDeepDive}</p>}
        </div>
      )}

      {Array.isArray(result.dimensions) && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-semibold" data-testid="dimensions-title">{t("res.dimensions")}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {result.dimensions.map((d) => (
              <Bar key={d.key} label={pick(d, "label")} pct={d.pct} band={d.band} />
            ))}
          </div>
        </section>
      )}

      {!Array.isArray(result.dimensions) && result.dimensions && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-semibold">{t("res.dimensions")}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {Object.values(result.dimensions).map((d) => (
              <Bar key={d.dimension} label={`${d.posLetter} / ${d.negLetter} → ${d.letter}`} pct={d.percent} />
            ))}
          </div>
        </section>
      )}

      {Array.isArray(result.careers) && result.careers.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-semibold" data-testid="careers-title">{t("res.careers")}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {result.careers.slice(0, 8).map((c) => (
              <Bar key={c.key} label={pick(c, "label")} pct={c.pct} color="#0891b2" />
            ))}
          </div>
        </section>
      )}

      <section className="mt-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-semibold" data-testid="report-title">{t("report.title")}</h2>
          <div className="flex gap-2 no-print">
            {!shared && (
              <Button variant="outline" size="sm" onClick={doShare} data-testid="share-report-button">
                {copied ? <Check className="mr-1 h-4 w-4" /> : <Share2 className="mr-1 h-4 w-4" />}{t("res.share")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="download-pdf-button">
              {pdfBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />}{t("res.pdf")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-report-button">
              <Printer className="mr-1 h-4 w-4" />{t("res.print")}
            </Button>
          </div>
        </div>
        {shareUrl && !shared && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-2 text-xs no-print" data-testid="share-link-box">
            <span className="truncate text-muted-foreground">{shareUrl}</span>
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="shrink-0 font-semibold text-primary hover:underline">{t("res.copy")}</button>
          </div>
        )}

        {isNew && !shared && Object.keys(reportLangs).length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="report-lang-status">
            {["en", "hi", "ta"].map((lk) => {
              const st = reportLangs[lk] || "none";
              const nm = { en: "English", hi: "हिंदी", ta: "தமிழ்" }[lk];
              const dot = st === "ready" ? "bg-emerald-500" : st === "pending" ? "bg-amber-400 animate-pulse" : "bg-rose-500";
              return (
                <div key={lk} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs" data-testid={`report-lang-${lk}`}>
                  <span className={`h-2 w-2 rounded-full ${st === "none" ? "bg-muted-foreground/40" : dot}`} />
                  <span className="font-medium">{nm}</span>
                  {(st === "failed") && (
                    <button
                      onClick={async () => { await api.post(`/api/results/${id}/generate-report?lang=${lk}`); load(); }}
                      className="ml-1 font-semibold text-primary hover:underline"
                      data-testid={`retry-lang-${lk}`}
                    >retry</button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {langStatus === "pending" && (
          <div className="mt-5 rounded-2xl border border-border bg-card p-6" data-testid="report-generating">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />{t("res.generating")}
            </div>
            {stuckTooLong && !shared && (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="report-stuck-hint">
                <span>{t("res.stuck")}</span>
                <Button
                  variant="outline" size="sm"
                  onClick={async () => { await api.post(`/api/results/${id}/generate-report?lang=${langKey}`); load(); }}
                  data-testid="report-retry-stuck"
                >{t("res.retry")}</Button>
              </div>
            )}
          </div>
        )}

        {isNew && !shared && (langStatus === "failed" || langStatus === "none") && (
          <div className="mt-5 rounded-2xl border border-border bg-card p-6 no-print" data-testid="report-missing">
            <p className="mb-3 text-sm text-muted-foreground">
              {langStatus === "failed"
                ? `The ${langName} report couldn't be generated. Retry just this language:`
                : `Generate your ${langName} report:`}
            </p>
            <Button onClick={async () => { await api.post(`/api/results/${id}/generate-report?lang=${langKey}`); load(); }} data-testid="generate-report-button">
              <Sparkles className="mr-1 h-4 w-4" />{t("res.generate")} ({langName})
            </Button>
          </div>
        )}

        {report && (
          <div className="mt-5 space-y-6" data-testid="ai-report">
            {report.introduction && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <p className="text-sm leading-relaxed">{report.introduction}</p>
                {Array.isArray(report.introHighlights) && (
                  <ul className="mt-4 space-y-2">
                    {report.introHighlights.map((h, i) => (
                      <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-primary">•</span>{h}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              {Array.isArray(report.strengths) && (
                <div className="rounded-2xl border border-border bg-card p-6 print-full">
                  <h3 className="font-display text-lg font-semibold text-primary">{t("report.strengths")}</h3>
                  <ul className="mt-3 space-y-3">
                    {report.strengths.map((s, i) => (
                      <li key={i}><div className="text-sm font-semibold">{s.title}</div><div className="text-xs text-muted-foreground">{s.detail}</div></li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(report.weaknesses) && (
                <div className="rounded-2xl border border-border bg-card p-6 print-full">
                  <h3 className="font-display text-lg font-semibold text-amber-600">{t("report.weaknesses")}</h3>
                  <ul className="mt-3 space-y-3">
                    {report.weaknesses.map((s, i) => (
                      <li key={i}><div className="text-sm font-semibold">{s.title}</div><div className="text-xs text-muted-foreground">{s.detail}</div></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {report.learningStyle && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.learning")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{report.learningStyle}</p>
              </div>
            )}
            {report.recommendedStream && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.stream")}</h3>
                <p className="mt-2 text-sm font-semibold text-primary">{report.recommendedStream.stream}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{report.recommendedStream.why}</p>
              </div>
            )}
            {report.higherEducation?.summary && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.higherEd")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{report.higherEducation.summary}</p>
              </div>
            )}
            {report.careerPlanning?.summary && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.career")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{report.careerPlanning.summary}</p>
              </div>
            )}
            {Array.isArray(report.skills) && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.skills")}</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {report.skills.map((s) => <span key={s} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">{s}</span>)}
                </div>
              </div>
            )}
            {report.growth && (
              <div className="rounded-2xl border border-border bg-card p-6 print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.growth")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{report.growth}</p>
              </div>
            )}
            {report.conclusion && (
              <div className="rounded-2xl bg-primary p-6 text-primary-foreground print-full">
                <h3 className="font-display text-lg font-semibold">{t("report.conclusion")}</h3>
                <p className="mt-2 text-sm leading-relaxed opacity-90">{report.conclusion}</p>
              </div>
            )}
          </div>
        )}
      </section>
      </div>

      {shared ? (
        <div className="mt-10 text-center no-print" data-testid="shared-footer">
          <p className="text-sm text-muted-foreground">{t("res.sharedBy")}</p>
          <Link to="/"><Button variant="outline" className="mt-3 rounded-full" data-testid="shared-cta">{t("hero.cta.test")}</Button></Link>
        </div>
      ) : (
        <div className="mt-10 text-center no-print">
          <Link to="/assessment"><Button variant="outline" className="rounded-full" data-testid="retake-button">{t("dash.new")}</Button></Link>
        </div>
      )}
    </div>
  );
}
